<script setup lang="ts">
import { onMounted, onBeforeUnmount, ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { VButton, VDropdownMultiple, VIcon, VTextArea, VTextField } from '@/modules/shared/components'
import VSelect from '../../../components/v-select.vue'
import type { ExistingFile } from '@/modules/shared/components/the-uploader.vue'
import CompanySearchField, { type EnrichCandidate } from '../../../components/company-search-field.vue'
import DropZone from '@/modules/filing/components/DropZone.vue'
import { useCompanyStore } from '../../../stores/company-store'
import { useFileDrop } from '@/modules/filing/composables/use-file-drop'
import { useFetch } from '@/modules/core/composables/useFetch'

const props = defineProps<{
  form: any
  v$: any
  investmentTypeOptions: { label: string; value: string }[]
  sectorOptions: { label: string; value: string }[]
  visibilityOptions: { label: string; value: string }[]
  investmentStageOptions: { label: string; value: string }[]
  investorOptions: { label: string; value: string }[]
  files: File[]
  existingFiles: ExistingFile[]
}>()

const emit = defineEmits<{
  (e: 'update:files', files: File[]): void
  (e: 'remove:existing-file', fileId: string): void
}>()

const route = useRoute()
const slug = route.params.slug as string
const companyStore = useCompanyStore()

const COMPANY_FIELDS = [
  'company_name',
  'company_id',
  'company_description',
  'company_website',
  'sector',
  'investment_stage',
  'type_of_investment',
  'other_investment_type',
] as const

const companySnapshot = ref<Record<string, unknown> | null>(null)

onMounted(() => {
  companyStore.fetchCompanies()
  if (props.form.company_name) {
    fillSource.value = 'existing'
  }
  companySnapshot.value = Object.fromEntries(COMPANY_FIELDS.map((f) => [f, props.form[f]]))
})

onBeforeUnmount(() => {
  if (fillSource.value === null && companySnapshot.value) {
    Object.assign(props.form, companySnapshot.value)
  }
})

///////////////////////////////////////////////////////////////////////////////
// Progressive disclosure
///////////////////////////////////////////////////////////////////////////////

type FillSource = 'local' | 'web' | 'manual' | 'existing' | null

const fillSource = ref<FillSource>(null)
const fieldsRevealed = computed(() => fillSource.value !== null)

const bannerMessage = computed(() => {
  switch (fillSource.value) {
    case 'local':
      return 'Filled from your company records. You can edit any field below.'
    case 'web':
      return 'Auto-filled from web search. Review and edit as needed.'
    default:
      return null
  }
})

///////////////////////////////////////////////////////////////////////////////
// Lookup trigger
///////////////////////////////////////////////////////////////////////////////

const hasLocalMatches = computed(() => {
  const q = props.form.company_name?.trim().toLowerCase()
  if (!q || q.length < 2) return false
  return companyStore.companies.some((c) => c.name.toLowerCase().includes(q))
})

const showLookup = computed(() => {
  const name = props.form.company_name?.trim()
  if (!name || name.length < 2) return false
  if (fillSource.value) return false
  if (hasLocalMatches.value) return false
  if (lookupLoading.value) return false
  return true
})

///////////////////////////////////////////////////////////////////////////////
// Lookup
///////////////////////////////////////////////////////////////////////////////

const lookupLoading = ref(false)
const lookupError = ref<string | null>(null)
const lookupCandidates = ref<EnrichCandidate[]>([])

const onCompanyLookup = async () => {
  const name = props.form.company_name?.trim()
  if (!name || lookupLoading.value) return

  lookupLoading.value = true
  lookupError.value = null
  lookupCandidates.value = []
  try {
    const data = await useFetch(`/v4/${slug}/investing/companies/enrich`).post<{
      candidates: EnrichCandidate[]
    }>({ name })

    const candidates = data?.candidates ?? []

    if (!candidates.length) {
      lookupError.value = "Couldn't find this company on the web. You can fill in the details manually."
      fillSource.value = 'manual'
      return
    }

    if (candidates.length === 1) {
      applyCandidate(candidates[0])
    } else {
      lookupCandidates.value = candidates
    }
  } catch {
    lookupError.value = "Couldn't auto-fill company details. Please fill them manually."
    fillSource.value = 'manual'
  } finally {
    lookupLoading.value = false
  }
}

const applyCandidate = (candidate: EnrichCandidate) => {
  if (candidate.name) props.form.company_name = candidate.name
  if (candidate.description) props.form.company_description = candidate.description
  if (candidate.website) props.form.company_website = candidate.website
  if (candidate.sector) props.form.sector = candidate.sector
  if (candidate.investment_stage) props.form.investment_stage = candidate.investment_stage
  lookupCandidates.value = []
  fillSource.value = 'web'
}

///////////////////////////////////////////////////////////////////////////////
// Local company selection
///////////////////////////////////////////////////////////////////////////////

const onCompanySelect = (company: { id: string; name: string }) => {
  props.form.company_id = company.id
  const full = companyStore.companies.find((c) => c.id === company.id)
  if (full) {
    props.form.company_description = full.elevator_pitch ?? props.form.company_description
    props.form.company_website = full.website ?? props.form.company_website
  }
  fillSource.value = 'local'
}

const onCompanyClear = () => {
  props.form.company_id = null
  props.form.company_description = null
  props.form.company_website = null
  props.form.sector = null
  props.form.investment_stage = null
  props.form.type_of_investment = null
  props.form.other_investment_type = null
  fillSource.value = null
  lookupError.value = null
  lookupCandidates.value = []
}

const onFillManually = () => {
  fillSource.value = 'manual'
  lookupCandidates.value = []
}

///////////////////////////////////////////////////////////////////////////////
// File uploads
///////////////////////////////////////////////////////////////////////////////

const SUPPORTING_DOC_EXTENSIONS = [
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.ppt',
  '.pptx',
  '.txt',
  '.csv',
  '.jpg',
  '.jpeg',
  '.png',
  '.heic',
]

const { fileInput, removeFile, onDrop, onFileSelect, formatSize } = useFileDrop(
  () => props.files,
  (files) => emit('update:files', files),
  SUPPORTING_DOC_EXTENSIONS,
)

const fileExtension = (name: string): string => name.split('.').pop()?.toUpperCase() ?? 'FILE'

defineExpose({
  isValid: async () => {
    const fields = ['company_name']
    const results = await Promise.all(fields.filter((f) => props.v$[f]).map((f) => props.v$[f].$validate()))
    return results.every(Boolean)
  },
})
</script>

<template>
  <div class="space-y-6">
    <CompanySearchField
      v-model="form.company_name"
      label="Company Name"
      placeholder="Search existing companies or create a new one"
      property="company_name"
      :v$="v$"
      :show-lookup="showLookup"
      :lookup-loading="lookupLoading"
      @select="onCompanySelect"
      @clear="onCompanyClear"
      @lookup="onCompanyLookup"
    />

    <!-- Candidates from web search -->
    <div v-if="lookupCandidates.length" class="space-y-3">
      <p class="text-sm text-gray-600">We found multiple companies. Which one did you mean?</p>
      <div class="space-y-2">
        <button
          v-for="(candidate, idx) in lookupCandidates"
          :key="idx"
          type="button"
          class="group flex w-full items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 text-left transition-colors hover:border-sky-300 hover:bg-sky-50"
          @click="applyCandidate(candidate)"
        >
          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-2">
              <span class="text-sm font-medium text-gray-800">{{ candidate.name }}</span>
              <span
                v-if="candidate.sector"
                class="inline-flex shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500"
              >
                {{ candidate.sector }}
              </span>
            </div>
            <p v-if="candidate.description" class="mt-0.5 line-clamp-1 text-xs text-gray-400">
              {{ candidate.description }}
            </p>
            <span v-if="candidate.website" class="mt-0.5 block text-xs text-gray-400">
              {{ candidate.website?.replace(/^https?:\/\/(www\.)?/, '') }}
            </span>
          </div>
          <svg
            class="h-4 w-4 shrink-0 text-gray-300 transition-colors group-hover:text-sky-400"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
          </svg>
        </button>
      </div>
      <p class="text-sm text-gray-400">
        None of these?
        <a href="#" class="text-sky-600 hover:text-sky-700" @click.prevent="onFillManually">Fill in manually</a>
      </p>
    </div>

    <!-- Lookup error -->
    <p v-if="lookupError" class="text-sm text-amber-600">{{ lookupError }}</p>

    <!-- Source banner -->
    <div v-if="bannerMessage" class="flex items-center gap-2 rounded-md bg-sky-50 px-3 py-2 text-sm text-sky-700">
      <svg
        class="h-4 w-4 flex-shrink-0"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke-width="1.5"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"
        />
      </svg>
      {{ bannerMessage }}
    </div>

    <!-- Company detail fields — hidden until company is identified -->
    <template v-if="fieldsRevealed">
      <VTextArea
        v-model="form.company_description"
        label="Company Description"
        placeholder="Brief description of the company and what they do"
        property="company_description"
        :v$="v$"
      />
      <VTextField
        v-model="form.company_website"
        label="Company Website"
        placeholder="https://"
        property="company_website"
        :v$="v$"
      />
      <VSelect v-model="form.sector" label="Sector" :options="sectorOptions" property="sector" :v$="v$" />
      <VSelect
        v-model="form.type_of_investment"
        label="Type of Investment"
        :options="investmentTypeOptions"
        property="type_of_investment"
        :v$="v$"
      />
      <VTextField
        v-if="form.type_of_investment === 'Other'"
        v-model="form.other_investment_type"
        label="Other investment type"
        property="other_investment_type"
        :v$="v$"
      />
      <VSelect
        v-model="form.investment_stage"
        label="Investment Stage"
        :options="investmentStageOptions"
        property="investment_stage"
        :v$="v$"
      />
      <div class="grid gap-4 md:grid-cols-2">
        <VTextField
          v-model="form.raised_amount"
          type="currency"
          label="Total Being Raised"
          property="raised_amount"
          :v$="v$"
        />
        <VTextField
          v-model="form.pre_money_valuation_amount"
          type="currency"
          label="Pre-Money Valuation"
          property="pre_money_valuation_amount"
          :v$="v$"
        />
      </div>
      <VTextField
        v-model="form.minimum_investment_amount"
        type="currency"
        label="Minimum Investment"
        property="minimum_investment_amount"
        :v$="v$"
      />
      <VTextField
        v-model="form.expected_close_date"
        type="date"
        label="Expected Closing Date"
        property="expected_close_date"
        :v$="v$"
      />

      <div class="my-8 h-px bg-gray-100"></div>

      <p class="text-sm font-medium text-gray-700">Deal Visibility</p>
      <VSelect
        v-model="form.visibility"
        label="Who can see this deal?"
        :options="visibilityOptions"
        property="visibility"
        :v$="v$"
      />
      <fieldset
        v-if="form.visibility === 'selected'"
        class="space-y-4 rounded-lg border border-gray-300/50 bg-white p-4"
      >
        <label class="block text-sm font-medium text-gray-800">Select investors</label>
        <VDropdownMultiple
          aligned="left"
          class="z-auto"
          :options="investorOptions"
          property="invited_investor_ids"
          v-model="form.invited_investor_ids"
          :show-selected-list="true"
        >
          <VButton size="xl" class="w-full" type="button">
            <div class="flex items-center justify-between">
              <div>Select investors...</div>
              <VIcon name="ChevronSelectorVertical" />
            </div>
          </VButton>
        </VDropdownMultiple>
      </fieldset>

      <div class="my-8 h-px bg-gray-100"></div>

      <div>
        <h3 class="mb-1 text-sm font-medium text-gray-700">Supporting documents</h3>
        <p class="mb-3 text-sm text-gray-500">
          Upload pitch decks, term sheets, or other relevant documents for this investment.
        </p>

        <div v-if="existingFiles.length || files.length" class="mb-3 space-y-0">
          <div
            v-for="file in existingFiles"
            :key="'existing-' + file.id"
            class="flex items-center gap-2.5 border-b border-gray-50 py-2 last:border-0"
          >
            <div
              class="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded bg-red-50 text-[7px] font-extrabold text-red-600"
            >
              {{ fileExtension(file.name) }}
            </div>
            <div class="min-w-0 flex-1">
              <div class="truncate text-sm font-semibold text-gray-800">{{ file.name }}</div>
            </div>
            <button
              class="flex-shrink-0 rounded px-1.5 py-0.5 text-sm text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              @click="$emit('remove:existing-file', file.id)"
            >
              &times;
            </button>
          </div>
          <div
            v-for="(file, i) in files"
            :key="'new-' + i"
            class="flex items-center gap-2.5 border-b border-gray-50 py-2 last:border-0"
          >
            <div
              class="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded bg-red-50 text-[7px] font-extrabold text-red-600"
            >
              {{ fileExtension(file.name) }}
            </div>
            <div class="min-w-0 flex-1">
              <div class="truncate text-sm font-semibold text-gray-800">{{ file.name }}</div>
              <div class="text-xs text-gray-400">{{ formatSize(file.size) }}</div>
            </div>
            <button
              class="flex-shrink-0 rounded px-1.5 py-0.5 text-sm text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              @click="removeFile(i)"
            >
              &times;
            </button>
          </div>
        </div>

        <DropZone state="default" @click="fileInput?.click()" @drop.prevent="onDrop" @dragover.prevent />
        <input
          ref="fileInput"
          type="file"
          multiple
          :accept="SUPPORTING_DOC_EXTENSIONS.join(',')"
          class="hidden"
          @change="onFileSelect"
        />
      </div>
    </template>
  </div>
</template>
